/**
 * Run an async unit of work and guarantee a cleanup callback fires afterwards,
 * on both the resolve and throw paths.
 *
 * Why this exists as a standalone module function rather than an inline
 * `try { … } finally { … }`: React Compiler cannot yet lower a `TryStatement`
 * with a `finally` clause into its HIR, so any component or hook whose body
 * contains one is skipped entirely for automatic memoization (surfaced by
 * react-doctor's `react-hooks-js/todo`). Keeping the `finally` here — in a
 * plain module function the compiler never tries to memoize — lets the calling
 * component stay Compiler-eligible while preserving identical guaranteed-cleanup
 * semantics. Cold action handlers (form submit, modal open, wallet action) wrap
 * their body in `work` and pass the cleanup (e.g. `() => setSubmitting(false)`)
 * as `cleanup`; both close over component scope normally — no setter threading.
 */
export async function runWithCleanup<T>(work: () => Promise<T>, cleanup: () => void): Promise<T> {
  try {
    return await work();
  } finally {
    cleanup();
  }
}
